export const OVERVIEW_DOC = 'directory/overview.md';

export type OverviewLink =
  | {kind: 'external'; href: string}
  | {kind: 'anchor'; id: string}
  | {kind: 'doc'; doc: string; id?: string; href: string}
  | {kind: 'asset'; href: string};

const ABSOLUTE_URI = /^([a-z][a-z0-9+.-]*:|\/\/)/i;

/**
 * Base URL of the app's published `assets/` folder. Markdown docs are addressed by their path relative to it
 * (e.g. `directory/overview.md`), matching the files `validateAssets` checks.
 */
export function assetsRoot(overviewUrl: string): string {
  return overviewUrl.replace(/[^/]+\/[^/]+$/, '');
}

/**
 * Splits a `doc` query value (`directory/setup.md#install`) into a doc path and heading id. Paths that escape the
 * assets folder or aren't markdown fall back to the overview.
 */
export function parseDocParam(root: string, param?: string | null): {doc: string; id?: string} {
  if (!param) {
    return {doc: OVERVIEW_DOC};
  }
  const [path, ...rest] = param.split('#');
  const id = rest.length ? decodeHash(rest.join('#')) : undefined;
  const url = toUrl(path, root);
  if (!url || !isMarkdownUnder(url, root)) {
    return {doc: OVERVIEW_DOC};
  }
  return {doc: url.href.slice(root.length), id};
}

export function docUrl(root: string, doc: string): string {
  return new URL(doc, root).href;
}

export function docParam(doc: string, id?: string): string | undefined {
  if (doc === OVERVIEW_DOC && !id) {
    return undefined;
  }
  return id ? `${doc}#${encodeURIComponent(id)}` : doc;
}

/**
 * Classifies a markdown link the way `remark-validate-links` resolves it: relative to the file it appears in.
 */
export function resolveLink(href: string | undefined, root: string, currentDoc: string): OverviewLink {
  if (!href || ABSOLUTE_URI.test(href)) {
    return {kind: 'external', href: href || ''};
  }
  if (href.startsWith('#')) {
    return {kind: 'anchor', id: decodeHash(href.slice(1))};
  }
  const url = toUrl(href, docUrl(root, currentDoc));
  if (!url) {
    return {kind: 'external', href};
  }
  const id = url.hash ? decodeHash(url.hash.slice(1)) : undefined;
  url.hash = '';
  if (!isMarkdownUnder(url, root)) {
    return {kind: 'asset', href: url.href + (id ? `#${encodeURIComponent(id)}` : '')};
  }
  const doc = url.href.slice(root.length);
  if (doc === currentDoc && id) {
    return {kind: 'anchor', id};
  }
  return {kind: 'doc', doc, id, href: url.href};
}

export function resolveImageUri(src: string, root: string, currentDoc: string): string {
  if (ABSOLUTE_URI.test(src)) {
    return src;
  }
  return toUrl(src, docUrl(root, currentDoc))?.href ?? src;
}

function isMarkdownUnder(url: URL, root: string): boolean {
  return url.href.startsWith(root) && /\.md$/i.test(url.pathname);
}

function toUrl(href: string, base: string): URL | undefined {
  try {
    return new URL(href, base);
  } catch {
    return undefined;
  }
}

function decodeHash(hash: string): string {
  try {
    return decodeURIComponent(hash);
  } catch {
    return hash;
  }
}
